import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFacility } from '../contexts/FacilityContext';
import { useCanMoveMoney, MONEY_ROLE_HINT } from '../contexts/AuthContext';
import { fetchHolds, resolveHold } from '../services/api';
import { Skeleton } from '../components/Skeleton';
import './Admin.css';
import './Holds.css';

// The hold-review queue (plan Step 6 — D11, R8).
//
// An encounter that arrives incomplete is ACCEPTED AND HELD, never rejected:
// rejecting at ingest makes real A/R invisible and lets it age silently past
// timely filing. This is the screen that works those holds.
//
// 🔴 TWO THINGS THIS PAGE MUST NOT DO, both decided in the plan:
//
//   1. IT NEVER SHOWS A DEADLINE IT CANNOT DERIVE (R8). When the payer has a
//      contracted filing limit on file, the row shows a real date. When it does
//      not — which includes every hold with no resolved payer, i.e. most of them
//      — the row shows DAYS SINCE SERVICE and no date at all. A wrong deadline
//      is worse than no deadline: it turns "I should check this" into "I have
//      until the 14th". The server sends no date in that state; do not invent
//      one here.
//   2. IT NEVER ASSUMES `claim_status === 'held'` IDENTIFIES A HELD CLAIM
//      (S6-b). A revision hold is raised against a claim that may already be
//      generated, submitted or accepted, and it deliberately leaves that status
//      alone. So a row can read "Submitted" and still be on this queue. That
//      combination is correct and is rendered as such.
//
// The facility comes from the app's existing picker. Any authenticated staff
// user may work holds — this is routine billing work, not an admin tool.

const REASON_LABELS = {
  claim_form_unresolved: 'No claim form for this payer',
  coverage_missing: 'No coverage on file',
  coverage_termed: 'Coverage terminated',
  missing_prior_auth: 'Prior authorization missing',
  unknown_payer: 'Payer not recognized',
  missing_required_field: 'Missing required information',
  possible_existing_claim: 'May already be claimed',
  price_mismatch: 'Price does not match the fee schedule',
  revision_review: 'Revised by the source',
};

const REASON_HELP = {
  claim_form_unresolved:
    'Neither claim form (CMS-1500 or UB-04) is one this payer accepts, so no claim could be built. The encounter is stored on the facility’s default form. Fix the payer’s accepted forms on the Payers page, or confirm the form on the claim, then resolve this hold.',
  coverage_missing:
    'The encounter arrived with no insurance on file for this patient. Add the coverage, then resolve this hold.',
  coverage_termed:
    'The coverage on file had ended by the date of service. Get current coverage, or bill the patient.',
  missing_prior_auth:
    'This payer requires prior authorization for this code and none was supplied. Call the reviewer shown (often a radiology benefit manager, not the payer), get the number, and add it to the claim. Retro-authorization windows are short.',
  unknown_payer:
    'The source named a payer this system has no mapping for. Map it on the Payers page, then resolve this hold.',
  missing_required_field:
    'The encounter is missing information the claim cannot be built without. Fill it in on the claim, then resolve this hold.',
  possible_existing_claim:
    'A claim already exists for this patient and service date with no source encounter attached — probably keyed in by hand. Compare the two and either link them or keep this one.',
  price_mismatch:
    'The charge the source reported differs from this facility’s fee schedule. The fee schedule is authoritative; confirm which is right.',
  revision_review:
    'The source sent a changed version of an encounter that has already produced a claim. NOTHING WAS APPLIED — the claim is untouched. Review the change and, if the claim needs correcting, use Correct or Void on the claim itself.',
};

// Resolutions offered per reason. `link_to_existing` belongs to the cutover-tail
// hold alone: it records that a DIFFERENT claim absorbed this encounter, which is
// a different outcome from "applied" and is audited as such.
const RESOLUTIONS = {
  applied: { label: 'Resolved — fixed', hint: 'The blocking condition was dealt with.' },
  rejected: { label: 'Rejected', hint: 'This should not be billed as it stands.' },
  dismissed: { label: 'Dismiss', hint: 'Not a real problem; take it off the queue.' },
  link_to_existing: { label: 'Linked to the existing claim', hint: 'The other claim covers this encounter.' },
  // ⟳ R32-3 (sync review, sixth pass): an identity change is carried out, not just recorded.
  move_to_source_patient: {
    label: 'Move to the patient the source names',
    hint: 'The claim and its payments move to that patient. The change shown above is then applied as the source sent it (or held, if the claim is coded), and every billing check runs again for the new patient.',
  },
  release_encounter: {
    label: 'Release the encounter (claim is voided)',
    hint: 'The voided claim gives up its source link, so the facility’s next send creates the claim correctly.',
  },
};

// Money moves with the two identity actions, so they take the money role (M6).
export const IDENTITY_RESOLUTIONS = ['move_to_source_patient', 'release_encounter'];

const isIdentityChange = (hold) =>
  hold.reason === 'revision_review'
  && Array.isArray(hold.detail?.changedFields)
  && hold.detail.changedFields.includes('source_patient_id');

export const resolutionsFor = (hold) => {
  if (hold.reason === 'possible_existing_claim') return ['link_to_existing', 'applied', 'rejected', 'dismissed'];
  const base = ['applied', 'rejected', 'dismissed'];
  if (hold.reason !== 'revision_review') return base;
  if (hold.claim_status === 'voided') return ['release_encounter', ...base];
  if (isIdentityChange(hold) && ['ready', 'held'].includes(hold.claim_status)) {
    return ['move_to_source_patient', ...base];
  }
  return base;
};

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt) ? String(d).slice(0, 10) : dt.toLocaleDateString();
};

const errorText = (err, fallback) =>
  err.response?.data?.error?.message || err.response?.data?.error || fallback;

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * 🔴 R8, rendered. Three states and never a fourth.
 *
 * The `deadline` branch is the only one that prints a date, and it prints the
 * payer's name beside it so the biller can see WHOSE limit it is. The `elapsed`
 * branch prints days since service and nothing else — the server does not send a
 * date in that state, deliberately.
 */
function FilingCell({ filing }) {
  if (!filing || filing.state === 'unknown') {
    return <span className="holds-muted">No service date</span>;
  }

  if (filing.state === 'deadline') {
    const overdue = filing.days_remaining < 0;
    return (
      <div className={`holds-filing holds-filing--deadline${filing.escalated ? ' is-escalated' : ''}`}>
        <span className="holds-filing-main">{fmtDate(filing.deadline_date)}</span>
        <span className="holds-filing-sub">
          {overdue
            ? `${plural(Math.abs(filing.days_remaining), 'day')} past the limit`
            : `${plural(filing.days_remaining, 'day')} left`}
          {' · '}{filing.payer_name}
        </span>
      </div>
    );
  }

  // Elapsed. No date — none is known, and guessing one is the failure mode this
  // whole design exists to prevent.
  return (
    <div className={`holds-filing holds-filing--elapsed${filing.escalated ? ' is-escalated' : ''}`}>
      <span className="holds-filing-main">{plural(filing.days_elapsed, 'day')} since service</span>
      <span className="holds-filing-sub">No filing limit on file</span>
    </div>
  );
}

// Sync plan Step 16 (S16-e): what changed, rendered as prior → incoming by RevisionChanges
// below, never as a row of raw JSON.
// R33-2: `pendingRevision` is the delivery's stored billing half, applied by the server
// when the encounter is moved; its changes are already shown in the prior → incoming table.
const REVISION_CHANGE_KEYS = ['changedFields', 'priorValues', 'incomingValues', 'pendingRevision'];

// ⟳ R34-2: a boolean flag reads as a sentence, never as "true".
const FLAG_TEXT = {
  revisionIdReused: 'Yes: another amendment already used this revision id with different content. Ask the source which one is current.',
};

function detailValue(key, value) {
  if (typeof value === 'boolean') return value ? (FLAG_TEXT[key] || 'Yes') : 'No';
  return Array.isArray(value) ? value.join(', ') : String(value ?? '—');
}

function DetailList({ detail }) {
  if (!detail || Object.keys(detail).length === 0) return null;
  const rows = Object.entries(detail)
    // 🔴 D27: the candidate CLAIM IDS are rendered as the candidate table below,
    // not as a row of bare numbers. Opaque ids are not something a biller can
    // compare against anything.
    // ⟳ R31-4: a revision hold can carry them too — rendered by RevisionCandidates.
    .filter(([key]) => key !== 'candidateClaimIds')
    .filter(([key]) => !REVISION_CHANGE_KEYS.includes(key))
    .map(([key, value]) => [key, detailValue(key, value)]);
  if (rows.length === 0) return null;
  return (
    <dl className="holds-detail">
      {rows.map(([key, value]) => (
        <div key={key}>
          <dt>{DETAIL_LABELS[key] || key}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

const CHANGED_FIELD_LABELS = {
  primary_procedure_code: 'Procedure code',
  primary_procedure_code_system: 'Code system',
  primary_procedure_modifiers: 'Modifiers',
  units: 'Units',
  service_date: 'Date of service',
  bill_insurance: 'Billing type',
  rendering_provider_npi: 'Rendering NPI',
  referring_provider_npi: 'Referring NPI',
  interpreting_provider_npi: 'Interpreting NPI',
  prior_authorization_number: 'Prior authorization',
  // ⟳ R31-2: the encounter was re-sent under a different source patient.
  source_patient_id: 'Source patient ID',
  // ⟳ 2026-10-07: the facility's own self-pay price changed after the claim was produced.
  source_reported_charge: 'Self-pay charge',
};

export function formatChangedValue(field, value) {
  if (value === null || value === undefined || value === '') return 'None';
  if (field === 'bill_insurance') return Number(value) === 1 ? 'Bill insurance' : 'Self-pay';
  if (field === 'source_reported_charge') return `$${value}`;
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

/**
 * Sync plan Step 16 (S16-e, acceptance criterion 1): a revision the source sent with no
 * revision identity, shown as WHAT CHANGED. "Before" is what the source said last time,
 * which is not always what the claim now shows: a biller may have edited the claim since.
 * Nothing here was applied to the claim.
 */
export function RevisionChanges({ detail }) {
  const changed = Array.isArray(detail?.changedFields) ? detail.changedFields : [];
  if (changed.length === 0) return null;
  return (
    <div className="holds-changes">
      <table className="holds-changes-table">
        <caption>What the source changed</caption>
        <thead>
          <tr>
            <th scope="col">Field</th>
            <th scope="col">Source said before</th>
            <th scope="col">Source says now</th>
          </tr>
        </thead>
        <tbody>
          {changed.map((field) => (
            <tr key={field}>
              <th scope="row">{CHANGED_FIELD_LABELS[field] || field}</th>
              <td>{formatChangedValue(field, detail.priorValues?.[field])}</td>
              <td className="holds-changes-incoming">{formatChangedValue(field, detail.incomingValues?.[field])}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="holds-detail-note">
        &ldquo;Before&rdquo; is what the source sent last time. The claim may show something else
        if it was edited here. None of these changes were made to the claim.
      </p>
    </div>
  );
}

/**
 * ⟳ R31-4 (fifth implementation review): a revision that moves the date of service onto a
 * hand-keyed claim's date is held, and names those claims. Read-only: there is no link
 * action here — the reviewer compares, then corrects or voids through the claim itself.
 */
export function RevisionCandidates({ hold }) {
  const ids = hold.detail?.candidateClaimIds;
  if (!Array.isArray(ids) || ids.length === 0) return null;
  const candidates = hold.candidates ?? [];
  return (
    <div className="holds-changes">
      <span className="holds-candidates-label">
        Hand-keyed claim{ids.length === 1 ? '' : 's'} already on the new date
      </span>
      {candidates.length === 0 ? (
        <p className="holds-muted">The claims this hold named are no longer at this facility.</p>
      ) : (
        <ul className="holds-candidate-list">
          {candidates.map((candidate) => (
            <li key={candidate.claim_id} className="holds-candidate">
              <span className="holds-candidate-body">
                <span className="holds-candidate-head">
                  <strong>{candidate.claim_number}</strong>
                  <span className={`holds-status holds-status--${candidate.claim_status}`}>
                    {candidate.claim_status}
                  </span>
                </span>
                <span className="holds-candidate-meta">
                  {candidate.patient_name}
                  {' · '}Service {fmtDate(candidate.service_date)}
                </span>
                <span className="holds-candidate-study">
                  <strong>{candidate.procedure_codes || 'Not coded yet'}</strong>
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * 🔴 D27's candidate display (plan Step 9, acceptance criterion 2; §4.11.1).
 *
 * The MUST this satisfies: the reviewer sees the PROCEDURE AND STUDY DESCRIPTION
 * on both sides, not just patient and date. At an imaging center those are the
 * only fields that tell two same-day encounters apart — a morning MRI and an
 * afternoon X-ray both match the same (facility, MRN, date) key and both hold
 * against the same manual claim — and a reviewer shown two identical-looking
 * rows will guess.
 *
 * The left column is the INCOMING encounter, which the RCM has nowhere else to
 * show: `sync_events` stores the response and never the request. The right is
 * every candidate claim we already hold.
 *
 * 🔴 IT NEVER PRESELECTS. The key is genuinely ambiguous, which is why D27 holds
 * instead of auto-linking; a preselected radio is auto-linking with extra steps.
 */
function CandidatePanel({ hold, selected, onSelect }) {
  const candidates = hold.candidates ?? [];
  const incomingCode = hold.detail?.procedureCode;
  const incomingStudy = hold.detail?.studyDescription;

  return (
    <div className="holds-candidates">
      <div className="holds-candidates-incoming">
        <span className="holds-candidates-label">This encounter</span>
        <div className="holds-candidates-study">
          <strong>{incomingCode || 'No procedure code'}</strong>
          <span>{incomingStudy || 'No study description on file for that code'}</span>
        </div>
      </div>

      <span className="holds-candidates-label">
        {candidates.length === 0
          ? 'Possible existing claims'
          : `Possible existing claim${candidates.length === 1 ? '' : 's'} (${candidates.length})`}
      </span>

      {candidates.length === 0 ? (
        <p className="holds-muted">
          The claims this hold named are no longer at this facility. Resolve it as
          &ldquo;Rejected&rdquo; to put this encounter back in the worklist.
        </p>
      ) : (
        <ul className="holds-candidate-list">
          {candidates.map((candidate) => (
            <li
              key={candidate.claim_id}
              className={`holds-candidate${selected === candidate.claim_id ? ' is-selected' : ''}${
                candidate.linkable ? '' : ' is-unlinkable'
              }`}
            >
              <label>
                <input
                  type="radio"
                  name={`holds-link-${hold.id}`}
                  value={candidate.claim_id}
                  checked={selected === candidate.claim_id}
                  disabled={!candidate.linkable}
                  onChange={() => onSelect(candidate.claim_id)}
                />
                <span className="holds-candidate-body">
                  <span className="holds-candidate-head">
                    <strong>{candidate.claim_number}</strong>
                    <span className={`holds-status holds-status--${candidate.claim_status}`}>
                      {candidate.claim_status}
                    </span>
                  </span>
                  <span className="holds-candidate-meta">
                    {candidate.patient_name}
                    {' · '}Service {fmtDate(candidate.service_date)}
                    {candidate.medical_record_number ? ` · MRN ${candidate.medical_record_number}` : ''}
                  </span>
                  <span className="holds-candidate-study">
                    <strong>{candidate.procedure_codes || 'Not coded yet'}</strong>
                    {candidate.procedure_descriptions ? ` — ${candidate.procedure_descriptions}` : ''}
                  </span>
                  {!candidate.linkable && (
                    <span className="holds-candidate-warning">
                      {candidate.already_linked
                        ? 'Another encounter has already been linked to this claim.'
                        : 'This claim is voided.'}
                    </span>
                  )}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}

      <p className="holds-detail-note">
        Compare the procedure and study description — at an imaging center they are the only
        fields that tell two same-day encounters apart. Linking voids this placeholder and moves
        the encounter onto the claim you pick; nothing is billed twice either way.
      </p>
    </div>
  );
}

const DETAIL_LABELS = {
  payerName: 'Payer',
  // claim_form_unresolved (review 2026-09-28): what the payer accepts, and the form the claim was stored on.
  acceptedFormTypes: 'Payer accepts',
  facilityDefaultForm: 'Stored on',
  coverageOrder: 'Coverage slot',
  terminationDate: 'Terminated',
  sourcePayerId: 'Source payer id',
  missingFields: 'Missing',
  candidateClaimIds: 'Possible existing claims',
  procedureCode: 'Procedure',
  studyDescription: 'Study',
  sourceReportedCharge: 'Source charge',
  expectedCharge: 'Fee schedule',
  toleranceCents: 'Tolerance (cents)',
  // price_mismatch, refined by sync plan Step 10 (S10-g): the signed difference, and which
  // rate column it was measured against — at a TC-only facility the component IS the reason.
  deltaCents: 'Difference (cents)',
  billingComponent: 'Priced as',
  // missing_prior_auth, refined by sync plan Step 10a: who reviews the request, and
  // the last day a retro-authorization can be asked for (only when the rule has one).
  authorityName: 'Reviewed by',
  retroAuthDeadline: 'Retro-auth by',
  storedRevisionId: 'Stored revision',
  incomingRevisionId: 'Incoming revision',
  sourceEncounterId: 'Source encounter',
  // ⟳ R32-1 / R32-2: the RCM patient the new source key resolved to, and when the source finalized this.
  incomingPatientId: 'Source now names patient #',
  incomingFinalizedAt: 'Source finalized',
  // ⟳ R34-2
  revisionIdReused: 'Revision id reused',
};

function HoldCard({ hold, onResolve, busy, onOpenClaim }) {
  const [open, setOpen] = useState(false);
  const [resolution, setResolution] = useState(resolutionsFor(hold)[0]);
  const [note, setNote] = useState('');
  // Never preselected — see CandidatePanel.
  const [linkClaimId, setLinkClaimId] = useState(null);

  const isCutover = hold.reason === 'possible_existing_claim';
  // M6: resolving a cutover hold runs the payment matcher, so it takes the money
  // role. Every other reason stays workable by any staff user (S6-c).
  const canMove = useCanMoveMoney();
  const roleBlocked = isCutover && !canMove;
  const needsTarget = resolution === 'link_to_existing';
  const blocked = (needsTarget && !linkClaimId) || (IDENTITY_RESOLUTIONS.includes(resolution) && !canMove);

  return (
    <article className={`holds-card${hold.filing?.escalated ? ' is-escalated' : ''}`}>
      <header className="holds-card-head">
        <div className="holds-card-title">
          <span className={`holds-reason holds-reason--${hold.reason}`}>
            {REASON_LABELS[hold.reason] || hold.reason}
          </span>
          {/* S6-b: a revision hold sits on a claim that kept its own status. */}
          {!hold.sets_claim_status && (
            <span className="holds-tag holds-tag--untouched">Claim unchanged</span>
          )}
          <span className={`holds-status holds-status--${hold.claim_status}`}>{hold.claim_status}</span>
        </div>
        <div className="holds-card-meta">
          <button type="button" className="holds-link" onClick={() => onOpenClaim(hold)}>
            {hold.claim_number}
          </button>
          {' · '}{hold.patient_name}
          {' · '}Service {fmtDate(hold.service_date)}
          {' · '}Held {plural(hold.age_days ?? 0, 'day')}
        </div>
      </header>

      <p className="holds-help">{REASON_HELP[hold.reason]}</p>

      <div className="holds-card-body">
        <DetailList detail={hold.detail} />
        {hold.reason === 'revision_review' && <RevisionChanges detail={hold.detail} />}
        {hold.reason === 'revision_review' && <RevisionCandidates hold={hold} />}
        {isCutover && (
          <CandidatePanel hold={hold} selected={linkClaimId} onSelect={setLinkClaimId} />
        )}
        <div className="holds-filing-wrap">
          <span className="holds-filing-label">Filing</span>
          <FilingCell filing={hold.filing} />
        </div>
      </div>

      {open ? (
        <footer className="holds-resolve">
          <label className="holds-field-label" htmlFor={`holds-res-${hold.id}`}>How was this resolved?</label>
          <select
            id={`holds-res-${hold.id}`}
            value={resolution}
            onChange={(e) => setResolution(e.target.value)}
          >
            {resolutionsFor(hold).map((key) => (
              <option key={key} value={key}>{RESOLUTIONS[key].label}</option>
            ))}
          </select>
          <p className="holds-resolve-hint">{RESOLUTIONS[resolution].hint}</p>

          {hold.reason === 'revision_review' && !IDENTITY_RESOLUTIONS.includes(resolution) && (
            <p className="holds-resolve-warning">
              Resolving this does not change the claim. If the claim itself needs correcting, use
              Correct or Void on the claim — this only clears it off the queue.
            </p>
          )}
          {IDENTITY_RESOLUTIONS.includes(resolution) && !canMove && (
            <p className="holds-resolve-warning">{MONEY_ROLE_HINT}</p>
          )}

          {/* 🔴 R9 made literal: the biller is told exactly what linking does to
              both claims, because it voids one of them. */}
          {needsTarget && (
            blocked ? (
              <p className="holds-resolve-warning">
                Pick which existing claim covers this encounter, above. Linking needs a target.
              </p>
            ) : (
              <p className="holds-resolve-warning">
                This encounter moves onto{' '}
                <strong>
                  {hold.candidates?.find((c) => c.claim_id === linkClaimId)?.claim_number ?? linkClaimId}
                </strong>
                , and {hold.claim_number} is voided. Nothing is deleted and nothing is billed twice.
              </p>
            )
          )}

          {isCutover && resolution === 'rejected' && (
            <p className="holds-resolve-warning">
              {hold.claim_number} goes back to the worklist as a separate claim. Only do this if it
              is genuinely a different visit from the ones above.
            </p>
          )}

          <textarea
            className="holds-note"
            rows={2}
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note — what you did, for whoever reads this later."
          />
          <div className="holds-resolve-actions">
            <button className="admin-modal-btn-cancel" onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </button>
            <button
              className="holds-btn-primary"
              disabled={busy || blocked}
              onClick={() => onResolve(hold, resolution, note.trim() || null, linkClaimId)}
            >
              {busy ? 'Saving…' : 'Resolve hold'}
            </button>
          </div>
        </footer>
      ) : (
        <footer className="holds-actions">
          <button className="holds-btn-primary" onClick={() => setOpen(true)} disabled={busy || roleBlocked} title={roleBlocked ? MONEY_ROLE_HINT : undefined}>
            Resolve…
          </button>
          <button className="holds-btn-secondary" onClick={() => onOpenClaim(hold)}>
            Open claim
          </button>
        </footer>
      )}
    </article>
  );
}

function Holds() {
  const { selectedFacility } = useFacility();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [reasonFilter, setReasonFilter] = useState('all');

  const facilityId = selectedFacility?.facility_id ?? selectedFacility?.id ?? null;

  const load = useCallback(async () => {
    if (!facilityId) { setItems([]); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetchHolds(facilityId);
      setItems(res.data || []);
    } catch (err) {
      setItems([]);
      setMsg({ type: 'error', text: errorText(err, 'Failed to load the hold queue.') });
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  const onResolve = async (hold, resolution, note, linkClaimId) => {
    setBusyId(hold.id);
    setMsg(null);
    try {
      const res = await resolveHold(hold.id, {
        resolution,
        note,
        ...(resolution === 'link_to_existing' ? { link_claim_id: linkClaimId } : {}),
      });
      const status = res.data?.claim_status;
      // A link is a different sentence from a clearance: one claim absorbed the
      // encounter and the other was voided, and saying "cleared — now voided"
      // would read as something having gone wrong.
      setMsg(
        res.data?.linked_claim_number
          ? {
              type: 'success',
              text:
                `Linked to ${res.data.linked_claim_number} — that claim now carries this encounter, ` +
                `and ${hold.claim_number} was voided. No second claim exists.`,
            }
          : {
              type: 'success',
              text: `Hold cleared on ${hold.claim_number}${status ? ` — the claim is now ${status}.` : '.'}`,
            }
      );
      await load();
    } catch (err) {
      setMsg({ type: 'error', text: errorText(err, 'The hold was not resolved. Nothing was changed.') });
    } finally {
      setBusyId(null);
    }
  };

  const reasons = [...new Set(items.map((item) => item.reason))];
  const shown = reasonFilter === 'all' ? items : items.filter((item) => item.reason === reasonFilter);
  const escalated = items.filter((item) => item.filing?.escalated).length;

  return (
    <div className="admin-page holds-page">
      <div className="admin-header">
        <div className="admin-header-text">
          <h1>Holds</h1>
          <p>
            Encounters accepted from the source but not yet billable. Each one is real revenue
            waiting on something — work the oldest first.
          </p>
        </div>
      </div>

      <div className="admin-toolbar">
        <label className="holds-filter">
          <span>Reason</span>
          <select value={reasonFilter} onChange={(e) => setReasonFilter(e.target.value)}>
            <option value="all">All reasons</option>
            {reasons.map((reason) => (
              <option key={reason} value={reason}>{REASON_LABELS[reason] || reason}</option>
            ))}
          </select>
        </label>
        {!loading && (
          <span className="holds-count">
            {plural(items.length, 'hold')}
            {escalated > 0 && <strong className="holds-count-escalated"> · {escalated} aging</strong>}
          </span>
        )}
      </div>

      {msg && (
        <div className={`admin-modal-alert ${msg.type}`} style={{ marginBottom: 20 }}>
          <span>{msg.text}</span>
        </div>
      )}

      {!facilityId ? (
        <div className="admin-empty">
          <h3>Pick a facility</h3>
          <p>Holds are listed per facility. Choose one from the facility picker above.</p>
        </div>
      ) : loading ? (
        <div className="holds-list" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div className="holds-card" key={i}>
              <Skeleton className="skeleton-text" style={{ height: 18, width: 280 }} />
              <Skeleton className="skeleton-text" style={{ height: 14, width: '60%', marginTop: 14 }} />
              <Skeleton style={{ height: 64, width: '100%', marginTop: 16, borderRadius: 10 }} />
            </div>
          ))}
        </div>
      ) : shown.length === 0 ? (
        <div className="admin-empty">
          <h3>{items.length === 0 ? 'Nothing is held' : 'Nothing with that reason'}</h3>
          <p>
            {items.length === 0
              ? 'Every encounter this facility has sent is billable.'
              : 'Clear the filter to see the rest of the queue.'}
          </p>
        </div>
      ) : (
        <div className="holds-list">
          {shown.map((hold) => (
            <HoldCard
              key={hold.id}
              hold={hold}
              busy={busyId === hold.id}
              onResolve={onResolve}
              onOpenClaim={(h) => navigate(`/claims/${h.claim_id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default Holds;
